import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Hidden,
  IconButton,
  Grid,
  TextField,
  Toolbar
} from '@material-ui/core';
import { alpha, styled } from '@material-ui/core/styles';
import closeFill from '@iconify/icons-eva/close-fill';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import { Icon } from '@iconify/react';
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import { AxiosResponseModel } from '../../@types/base';
import { CompanyListModel } from '../../@types/company';
import { getAllCompaniesWithReturn } from '../../redux/slices/company';
import { RootState } from '../../redux/store';
import { PATH_DASHBOARD } from '../../routes/paths';
import { MIconButton } from '../../components/@material-extend';
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';
import { AutoHideDuration } from '../../constants/snackbar.enum';
import LanguagePopover from './LanguagePopover';
import { Roles } from '../../constants/roles.enum';
import {
  setAdminToUserWhenSuperior,
  setAdminVirtualCompanyId,
  setUserToAdminWhenSuperior
} from '../../redux/slices/authJwt';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH + 1}px)`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5)
  }
}));

// ----------------------------------------------------------------------

type DashboardNavbarProps = {
  onOpenSidebar: VoidFunction;
};

export default function DashboardNavbar({
                                          onOpenSidebar
                                        }: DashboardNavbarProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { translate } = useLocales();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const { companyList } = useSelector((state: RootState) => state.company);
  const [companyId, setCompanyId] = useState('');

  // ----------------------------------------------------------------------

  const handleCompanyList = async () => {
    const result: AxiosResponseModel<CompanyListModel> = await getAllCompaniesWithReturn();

    if (result.status !== HttpStatusCodes.StatusSuccess) {
      if (result.status === HttpStatusCodes.StatusBadRequest) {
        enqueueSnackbar(result.data?.message, {
          variant: 'error',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        history.push(PATH_DASHBOARD.root);
      } else {
        enqueueSnackbar(translate('global.something.went.wrong'), {
          variant: 'error',
          autoHideDuration: AutoHideDuration.durationFiveSeconds,
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        // history.push(PATH_DASHBOARD.root);
      }
    }
  };

  // ----------------------------------------------------------------------

  useEffect(() => {
    if (!user.isSuperior && user.roles.includes(Roles.ADMIN)) {
      handleCompanyList();
    }
  }, [dispatch]);

  useEffect(() => {
    if (user.isSuperior) {
      history.push(PATH_DASHBOARD.general.alarm);
    }
  }, [user]);

  const handleCompanyChange = async (event: any, value: any) => {
    if (value) {
      setCompanyId(value.id?.toString() || '');
      // !HATA: burrada admin rolu değiştirilecek ve isSuperior alanı true yapılacak.
      const response = await setAdminVirtualCompanyId(value.id);
      if (response) {
        await setAdminToUserWhenSuperior(user);
        localStorage.setItem('superior', '1');
      }
    } else {
      setCompanyId('');
    }
  };

  const handleGoBackAdminDashboard = async () => {
    const response = await setAdminVirtualCompanyId(null);
    if (response) {
      const result = await setUserToAdminWhenSuperior(user);
      localStorage.setItem('superior', '0');
      if (result) {
        window.location.href = `${process.env.REACT_APP_SUPER_ADMIN_URL}/dashboard`;
      }
    }
  };
  // ----------------------------------------------------------------------

  return (
    <RootStyle>
      <ToolbarStyle>
        <Hidden lgUp>
          <IconButton
            onClick={onOpenSidebar}
            sx={{ mr: 1, color: 'text.primary' }}
          >
            <Icon icon={menu2Fill} />
          </IconButton>
        </Hidden>

        {/* <Searchbar /> */}
        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& > *:not(:first-of-type)': {
              ml: { xs: 1.5, sm: 2, lg: 3 }
            }
          }}
        >
          {!user.isSuperior && user.roles.includes(Roles.ADMIN) && (
            <Grid sx={{ minWidth: '200px' }}>
              <Autocomplete
                fullWidth
                options={companyList}
                getOptionLabel={(option: CompanyListModel) => option.name}
                onChange={handleCompanyChange}
                getOptionSelected={(option: CompanyListModel, value) =>
                  option.id === value.id
                }
                ListboxProps={{ style: { maxHeight: 150 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('global.top.bar.select.company')}
                    helperText={false}
                    margin="none"
                    size="small"
                  />
                )}
              />
            </Grid>
          )}

          {user.isSuperior && (user.roles.includes(Roles.USER) || user.roles.includes(Roles.CLIENT_ADMIN)) && (
            <Grid>
              <Button
                fullWidth
                size="small"
                color="inherit"
                variant="outlined"
                onClick={handleGoBackAdminDashboard}
                sx={{ color: 'black' }}
              >
                {translate('global.go.back.admin.dashboard')}
              </Button>
            </Grid>
          )}
          <LanguagePopover />
          {/* <NotificationsPopover /> */}
          {/* <AccountPopover /> */}
        </Box>
      </ToolbarStyle>
    </RootStyle>
  );
}
